import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, Subject, Subscription } from 'rxjs';

import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { AutoReminderRule } from '../../../models/auto-reminder-rule';

import { AutoReminderRuleService } from '../../../services/auto_rules/auto-reminder-rule.service';
import { NotyService } from '../../../services/common/noty.service';

@Component({
  selector: 'app-auto-reminder-rule-form',
  templateUrl: './auto-reminder-rule-form.component.html',
  styleUrls: ['./auto-reminder-rule-form.component.scss'],
})
export class AutoReminderRuleFormComponent implements OnInit {
  /**
   * Auto Reminder Rule id for edit
   * @type {number}
   */
  autoReminderRuleId: number;

  /**
   * Auto Reminder Rule being edited or new
   */
  autoReminderRule: AutoReminderRule = new AutoReminderRule();

  /**
   * Document type search term change
   * @type {Subject<string>}
   */
  documentTypeSearchTermChanged: Subject<string> = new Subject<string>();

  /**
   * Document type search term change subscription
   * @type {Subscription}
   */
  documentTypeSearchSubscription: Subscription;

  /**
   * Default form options
   * @type {any}
   */
  formOptions: any = {};

  /**
   * Indicates loading
   * @type {boolean}
   */
  loading: boolean = true;

  /**
   * Indicates if form is saving
   * @type {boolean}
   */
  saving: boolean = false;

  /**
   * Document type filter
   * @type {any}
   */
  documentTypeFilter: any = { showAs: 'list' };

  /**
   * Constructor
   * @param {NotyService}             private notyService             Notification service for alerts
   * @param {Location}                private location                Angular location object
   * @param {ActivatedRoute}          private route                   Angular current route
   * @param {Router}                  private router                  Angular router to navigate
   * @param {AutoReminderRuleService} private autoReminderRuleService Service to access auto reminder rule in the backend
   */
  constructor(private notyService: NotyService, private location: Location, private route: ActivatedRoute, private router: Router, private autoReminderRuleService: AutoReminderRuleService) {}

  // Component lifecycle methods

  /**
   * Called on component initialization
   */
  ngOnInit() {
    this.loading = true;
    this.autoReminderRuleService.formOptions().then((response_json) => {
      this.loading = false;
      this.formOptions = response_json;
      let documentTypes: any = {};
      for (let documentType of response_json.document_types) {
        documentType.children = [];
        documentType.expanded = true;
        documentTypes[documentType.id] = documentType;
      }
      let documentTypeList: Array<any> = [];
      for (let documentType of response_json.document_types)
        if (documentType.parent_id) {
          documentTypes[documentType.parent_id].children.push(documentType);
          documentType.parent = documentTypes[documentType.parent_id];
        } else documentTypeList.push(documentType);
      this.formOptions.document_types_tree = documentTypeList;
      this.autoReminderRuleId = this.route.snapshot.params['id'];
      if (this.autoReminderRuleId)
        this.autoReminderRuleService.edit(this.autoReminderRuleId).then((response_json) => {
          this.autoReminderRule = response_json.auto_reminder_rule;
          for (let documentTypeId of this.autoReminderRule.document_type_ids)
            for (let documentType of this.formOptions.document_types)
              if (documentTypeId == documentType.id) {
                documentType.selected = true;
                this.markSelectedDocumentTypeParents(documentType);
                break;
              }
        });
    });
    this.documentTypeSearchSubscription = this.documentTypeSearchTermChanged
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((search) => this.searchDocumentTypes())
      )
      .subscribe(() => {});
  }

  // Component methods

  /**
   * Navigates to previous page
   */
  back() {
    this.location.back();
  }

  /**
   * Mark parent document types of selected document type
   * @param {any} documentType Selected document type
   */
  markSelectedDocumentTypeParents(documentType: any) {
    let parentDocumentType: any = documentType.parent;
    while (parentDocumentType) {
      parentDocumentType.childSelected = true;
      parentDocumentType = parentDocumentType.parent;
    }
  }

  /**
   * Next document type term on change
   */
  nextDocumentTypeTerm() {
    if (this.documentTypeFilter.prefix) this.documentTypeFilter.showAs = 'list';
    this.documentTypeSearchTermChanged.next(this.documentTypeFilter.prefix);
  }

  /**
   * Search document types and return observable
   * @return {Observable<any>} Observable after searching for document types
   */
  searchDocumentTypes(): Observable<any> {
    for (let documentType of this.formOptions.document_types)
      if (String(documentType.prefix).toLowerCase().indexOf(this.documentTypeFilter.prefix) === 0) documentType.searchResult = true;
      else documentType.searchResult = false;
    return new Observable();
  }

  /**
   * Select document type
   * @param {any} documentType Selected document type
   */
  selectDocumentType(documentType: any) {
    this.setDocumentTypesSelection(documentType.children, documentType.selected || false);
  }

  /**
   * Select document types
   * @param {Array<any>} documentTypes Document types to select
   * @param {boolean =             false}       selection Indicates if selected or not
   */
  setDocumentTypesSelection(documentTypes: Array<any>, selection: boolean = false) {
    for (let documentType of documentTypes) {
      documentType.selected = selection;
      documentType.childSelected = false;
      this.markSelectedDocumentTypeParents(documentType);
      this.setDocumentTypesSelection(documentType.children || [], selection);
    }
  }

  /**
   * Save form
   */
  save() {
    this.saving = true;
    this.autoReminderRule.document_type_ids = [];
    for (let documentType of this.formOptions.document_types) if (documentType.selected) this.autoReminderRule.document_type_ids.push(documentType.id);
    this.autoReminderRuleService
      .save(this.autoReminderRule)
      .then((response_json) => {
        this.saving = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Auto reminder rule saved', 'success');
          this.router.navigateByUrl('/auto_reminder_rules');
        } else this.notyService.notify(response_json.text || 'Error saving auto reminder rule', 'error');
      })
      .catch(() => {
        this.saving = false;
        this.notyService.notify('Error saving auto reminder rule', 'error');
      });
  }
}
